import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
const logoWhite = "/logo-white.webp";
import { useLanguage } from "@/i18n/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

const TOTAL_FRAMES = 241;
const FRAME_URL_TEMPLATE =
  "https://lqhycpiamloexcrvmpmk.supabase.co/storage/v1/object/public/I%20Love%20Serum%20Hero%20Frames/iloveserum/frame_{frame}_delay-0.041s.webp";

function getFrameUrl(index: number): string {
  const padded = String(index).padStart(3, "0");
  return FRAME_URL_TEMPLATE.replace("{frame}", padded);
}

const LAST_FRAME_URL = getFrameUrl(TOTAL_FRAMES - 1);

interface HeroAnimationProps {
  onNavVisible: (visible: boolean) => void;
}

/* ─── Mobile: static last frame ─── */
const MobileHero = ({ onNavVisible }: HeroAnimationProps) => {
  const [loaded, setLoaded] = useState(false);
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  useEffect(() => { onNavVisible(true); }, [onNavVisible]);

  const isAr = lang === "ar";

  return (
    <div className="relative h-screen w-full overflow-hidden bg-background">
      {!loaded && <div className="absolute inset-0 z-10 bg-background" />}
      <img
        src={LAST_FRAME_URL}
        alt="Hero"
        onLoad={() => setLoaded(true)}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.4s ease" }}
      />
      <div
        className={`absolute inset-0 z-20 flex items-end ${isAr ? "justify-end" : "justify-start"} p-6 pb-10`}
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.4s ease" }}
      >
          <div className={`flex flex-col ${isAr ? "items-end text-right" : "items-start text-left"}`} dir={isAr ? "rtl" : "ltr"}>
            <img
              src={logoWhite}
              alt="Dr. Dee"
              className="h-10 w-auto drop-shadow-lg"
              draggable={false}
              fetchPriority="high"
              width={142}
              height={40}
            />
            <p
              className={`mt-2 text-sm font-normal text-white/75 ${isAr ? "max-w-[200px]" : "max-w-[280px]"}`}
              style={{ fontFamily, textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}
            >
              {t("hero.subtitle")}
            </p>
            <button
              onClick={() => navigate("/shop")}
              className="pointer-events-auto mt-5 rounded-full border border-white/40 px-8 py-3 text-sm font-bold text-white backdrop-blur-md transition-all duration-300 ease-out hover:shadow-[inset_0_2px_6px_rgba(255,255,255,0.8),0_0_30px_rgba(232,140,164,0.5)] active:scale-[0.97]"
              style={{
                fontFamily,
                background: "linear-gradient(180deg, rgba(232,140,164,0.85) 0%, rgba(210,110,140,0.8) 100%)",
                boxShadow: "inset 0 2px 4px rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.1), 0 8px 24px rgba(232,140,164,0.35)",
              }}
            >
              {t("hero.shopNow")}
            </button>
          </div>
      </div>
    </div>
  );
};

/* ─── Desktop: scroll-driven animation (original) ─── */
const DesktopHero = ({ onNavVisible }: HeroAnimationProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const currentFrameRef = useRef(0);
  const rafRef = useRef<number>();
  const [firstFrameReady, setFirstFrameReady] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const loadedCountRef = useRef(0);
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  const drawFrame = useCallback(
    (frameIndex: number, images?: HTMLImageElement[]) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const imgs = images || imagesRef.current;
      const img = imgs[frameIndex];
      if (!img) return;
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = window.devicePixelRatio || 1;
      const viewportWidth = parent.clientWidth;
      const viewportHeight = parent.clientHeight;
      canvas.width = viewportWidth * dpr;
      canvas.height = viewportHeight * dpr;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      const scale = Math.max(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
      const drawWidth = img.naturalWidth * scale;
      const drawHeight = img.naturalHeight * scale;
      const offsetX = (canvas.width - drawWidth) / 2;
      const offsetY = (canvas.height - drawHeight) / 2;
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, offsetX, offsetY, drawWidth, drawHeight);
    },
    []
  );

  useEffect(() => {
    const images: HTMLImageElement[] = new Array(TOTAL_FRAMES);
    imagesRef.current = images;
    const firstImg = new Image();
    firstImg.src = getFrameUrl(0);
    firstImg.onload = () => {
      images[0] = firstImg;
      loadedCountRef.current++;
      setFirstFrameReady(true);
      drawFrame(0, images);
      loadRemainingFrames();
    };
    firstImg.onerror = () => {
      loadedCountRef.current++;
      setFirstFrameReady(true);
      loadRemainingFrames();
    };
    function loadRemainingFrames() {
      for (let j = 1; j < TOTAL_FRAMES; j++) {
        const img = new Image();
        const idx = j;
        img.src = getFrameUrl(idx);
        img.onload = () => { images[idx] = img; loadedCountRef.current++; };
        img.onerror = () => { loadedCountRef.current++; };
      }
    }
  }, [drawFrame]);

  useEffect(() => {
    if (!firstFrameReady) return;
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollableHeight = container.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const progress = Math.min(Math.max(scrolled / scrollableHeight, 0), 1);
      setScrollProgress(progress);
      const frameIndex = Math.min(Math.floor(progress * (TOTAL_FRAMES - 1)), TOTAL_FRAMES - 1);
      if (frameIndex !== currentFrameRef.current) {
        currentFrameRef.current = frameIndex;
        const imgs = imagesRef.current;
        let drawIdx = frameIndex;
        if (!imgs[drawIdx]) {
          for (let d = 1; d < TOTAL_FRAMES; d++) {
            if (imgs[frameIndex - d]) { drawIdx = frameIndex - d; break; }
            if (imgs[frameIndex + d]) { drawIdx = frameIndex + d; break; }
          }
        }
        if (imgs[drawIdx]) {
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(() => drawFrame(drawIdx));
        }
      }
      const atStart = progress <= 0.005;
      const atEnd = progress >= 0.99;
      onNavVisible(atStart || atEnd);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [firstFrameReady, drawFrame, onNavVisible]);

  useEffect(() => {
    if (!firstFrameReady) return;
    const handleResize = () => drawFrame(currentFrameRef.current);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [firstFrameReady, drawFrame]);

  const atFirstFrame = scrollProgress <= 0.005;

  return (
    <div ref={containerRef} className="relative" style={{ height: "500vh" }}>
      <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden bg-background">
        {!firstFrameReady && <div className="absolute inset-0 z-10 bg-background" />}
        <canvas
          ref={canvasRef}
          className="block h-full w-full"
          style={{ opacity: firstFrameReady ? 1 : 0, transition: "opacity 0.4s ease" }}
        />
        <div
          className={`pointer-events-none absolute inset-0 z-20 flex items-center ${lang === "ar" ? "justify-end pr-[6%]" : "justify-end pr-[6%]"}`}
          style={{ opacity: firstFrameReady ? 1 : 0, transition: "opacity 0.4s ease" }}
        >
            <div className={`flex flex-col ${lang === "ar" ? "items-end text-right" : "items-start text-left"}`} dir={lang === "ar" ? "rtl" : "ltr"}>
              <img src={logoWhite} alt="Dr. Dee" className="h-14 w-auto drop-shadow-lg sm:h-16 md:h-20" draggable={false} fetchPriority="high" width={283} height={80} />
              <p className={`mt-3 text-base font-normal text-white/75 ${lang === "ar" ? "max-w-xs" : "max-w-sm"}`} style={{ fontFamily, textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}>
                {t("hero.subtitle")}
              </p>
              <button
                onClick={() => navigate("/shop")}
                className="pointer-events-auto mt-8 rounded-full border border-white/40 px-10 py-3.5 text-base font-bold text-white backdrop-blur-md transition-all duration-300 ease-out hover:px-14 hover:shadow-[inset_0_2px_6px_rgba(255,255,255,0.8),0_0_30px_rgba(232,140,164,0.5)] active:scale-[0.97]"
                style={{
                  fontFamily,
                  background: "linear-gradient(180deg, rgba(232,140,164,0.85) 0%, rgba(210,110,140,0.8) 100%)",
                  boxShadow: "inset 0 2px 4px rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.1), 0 8px 24px rgba(232,140,164,0.35)",
                }}
              >
              {t("hero.shopNow")}
              </button>
            </div>
        </div>
        {firstFrameReady && (
          <div
            className="absolute bottom-8 right-8 z-20 flex flex-col items-center gap-2 transition-opacity duration-500 ease-out"
            style={{ opacity: atFirstFrame ? 1 : 0, pointerEvents: "none" }}
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70" style={{ fontFamily, textShadow: "0 1px 6px rgba(0,0,0,0.3)" }}>
              {t("hero.scroll")}
            </span>
            <svg width="20" height="28" viewBox="0 0 20 28" fill="none" className="animate-bounce" style={{ filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.3))" }}>
              <path d="M10 2 L10 22 M3 16 L10 24 L17 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

const HeroAnimation = (props: HeroAnimationProps) => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileHero {...props} /> : <DesktopHero {...props} />;
};

export default HeroAnimation;
